import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, money } from '../api.js';
import { ReportHead, Money, Empty } from '../components/Shell.jsx';
import {
  DIRECTIONS, CONTRA_KINDS, acceptProposal, accountBalance, allocationsForPost, autoAllocates,
  bankAccounts, batchLevelReasons, blankRow, contraOptionLabel, contraOptions, counterpartyOptions, focusLabel, gcs, NEW_ACCOUNT_KINDS, newAccountBody,
  isMatchable, isPostable, isRowEmpty, lineBody, nextReferenceAfter, nextSort, patchBody,
  periodById, periodForDate, periodTag, proposalFor, proposalQuery, REFERENCE_MAX, rowFromLine,
  recleanMessage, rowProblem, settlementNote, sortLines, searchLines, PAGE_WIDTHS, loadPageWidth, savePageWidth, taxCodeOptions, unpostableByLine,
} from '../lib/cashBookGrid.js';
import {
  base64FromDataUrl, codingSplit, formatImportDateRange, hasVatRateOutliers, statementIsUnverified,
  UNVERIFIED_BALANCE_MESSAGE, vatOutlierMessage,
  holdingAccountOptions, holdingBody, loadHoldingChoice, saveHoldingChoice,
} from '../lib/cashBookImport.js';
import {
  clampWidth, effectiveWidths, gridColumns, loadWidths, safeStorage, saveWidths, tableWidthStyle,
  withoutWidth,
} from '../lib/columnWidths.js';
import {
  isAutoPreview, nextReferenceUrl, previewDate, rebaseDraftReferences, referenceAfterSave,
  referenceHint, referenceOnModeChange,
} from '../lib/generatedReference.js';

/**
 * Process cash books — the screen the owner works in every day.
 *
 * It is a deliberate reproduction of Sage Pastel Partner's *Process Cash
 * Books* (docs/pastel-cashbook-reference.png), because that is the screen the
 * owner asked to have replaced and the one their hands already know. The
 * column order, the two tabs, the two balances and the keyboard are all theirs.
 * What is NOT theirs is the arithmetic: every figure on this page was computed
 * by the server.
 *
 * ---------------------------------------------------------------------------
 * FOUR THINGS TO KNOW BEFORE CHANGING ANYTHING HERE.
 * ---------------------------------------------------------------------------
 *
 * 1. THE BROWSER DOES NO MONEY ARITHMETIC. api.js's header states the rule and
 *    routes/cashBook.js states it from the other side. The running total, the
 *    batch total and the VAT split all come out of `totals` in the response to
 *    the write that changed them — which is why every write here sets `totals`
 *    from what came back rather than adding a column up. The exclusive and
 *    inclusive amounts are the pair the bookkeeper typed and the server derived
 *    (decision 4); nothing on this page multiplies by 1.15.
 *
 * 2. THE RULES LIVE IN lib/cashBookGrid.js, not here. `npm test -w web` is
 *    node's own runner over `src/lib/*.test.js` and node cannot parse JSX, so
 *    anything decided in this file is a thing this repository cannot run. If
 *    you are about to write an `if` about what a row means, it belongs there.
 *
 * 3. THERE IS NO BATCH TOTAL FIELD, and there is not going to be one. The
 *    owner declined it on 28 Aug 2026 — "remove batch total. not needed" — and
 *    what replaced it is the live running total under the grid, which is the
 *    server's figure for the lines actually captured rather than a number
 *    somebody typed in for the screen to agree with.
 *
 * 4. `unpostable` IS SHOWN AGAINST THE ROW IT NAMES. The API sends a list of
 *    `{ lineNo, code, reason }` and not a boolean, precisely so a bookkeeper
 *    can be told WHICH row to fix. A banner saying "this batch cannot be
 *    posted" with no row attached throws that away.
 *
 * 5. A HISTORY SUGGESTION IS SHOWN AND NEVER APPLIED. `allocationRules.js:9-11`
 *    is the sentence — history alone never auto-posts — and this screen is one
 *    of the two places a human reads it. A confirmed RULE codes the line
 *    (server-side, on the save); a history guess sits in a warning strip with
 *    a button on it and reaches the row only when somebody presses that button.
 *    The badges are SlipReview.jsx's, word for word and class for class,
 *    because they are the same badge fed by the same two functions: `ok` for a
 *    rule, `note note--warn` for a history suggestion. Do not invent a second
 *    visual language for it here.
 */

const STATUS_LABEL = {
  open: 'Open — being captured',
  posted: 'Posted',
  abandoned: 'Abandoned',
};

/** What the two tabs mean, in the bookkeeper's own words. */
const DIRECTION_SUB = {
  payment: 'Money out of this cash book.',
  receipt: 'Money into this cash book.',
};

export function CashBook({ entity }) {
  const caps = entity?.capabilities ?? [];
  // `cashbook.post` is the authorisation half: the person who typed thirty
  // lines off a statement may not also be the one who commits them. A
  // client_admin holds capture alone, reaches this screen, and sees no Post
  // button at all — the API refuses either way, and this is about not offering
  // a button that answers 403.
  const canPost = caps.includes('cashbook.post');

  const [accounts, setAccounts] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [counterparties, setCounterparties] = useState([]);
  const [periods, setPeriods] = useState([]);
  // The trial balance is where BOTH of Pastel's balances come from — the cash
  // book's own in the header, and the contra account's in the status bar. One
  // report answers both, and it is the same figure the general ledger shows.
  const [tb, setTb] = useState([]);
  const [tbRefused, setTbRefused] = useState(false);
  const [batches, setBatches] = useState(null);

  const [bankAccountId, setBankAccountId] = useState('');
  const [direction, setDirection] = useState('payment');
  const [batchId, setBatchId] = useState(null);
  const [showBatches, setShowBatches] = useState(false);
  const [pageWidth, setPageWidth] = useState(() => loadPageWidth(safeStorage()));
  const [err, setErr] = useState(null);

  const loadBatches = useCallback(() => api.get(`/api/entities/${entity.id}/cash-batches`)
    .then((d) => setBatches(d.cashBatches))
    .catch((e) => { setErr(e.message); setBatches([]); }), [entity?.id]);

  const loadAccounts = useCallback(() => api.get(`/api/entities/${entity.id}/accounts`)
    .then((d) => {
      setAccounts(d.accounts);
      const banks = bankAccounts(d.accounts);
      if (banks.length > 0) setBankAccountId((cur) => cur || banks[0].id);
    })
    .catch((e) => setErr(e.message)), [entity?.id]);

  useEffect(() => {
    if (!entity?.id) return;
    loadAccounts();
    api.get(`/api/entities/${entity.id}/tax-codes`).then((d) => setTaxCodes(d.taxCodes)).catch(() => {});
    api.get(`/api/entities/${entity.id}/counterparties`)
      .then((d) => setCounterparties(d.counterparties)).catch(() => {});
    api.get(`/api/entities/${entity.id}`).then((d) => setPeriods(d.periods)).catch(() => {});
    loadBatches();
  }, [entity?.id, loadBatches, loadAccounts]);

  // The balances. `reports.ledger` is what a trial balance takes and a
  // client_admin does not hold it — so a refusal here is an ordinary state for
  // a real role, not an error to shout about. The screen says the balance is
  // unavailable rather than pretending it is zero.
  const loadBalances = useCallback(() => {
    if (!entity?.financial_year_start || !entity?.financial_year_end) return Promise.resolve();
    const q = `from=${entity.financial_year_start}&to=${entity.financial_year_end}`;
    return api.get(`/api/reports/${entity.id}/trial-balance?${q}`)
      .then((d) => { setTb(d.rows); setTbRefused(false); })
      .catch(() => { setTb([]); setTbRefused(true); });
  }, [entity?.id, entity?.financial_year_start, entity?.financial_year_end]);

  useEffect(() => { loadBalances(); }, [loadBalances]);

  const banks = bankAccounts(accounts);
  const bank = banks.find((a) => a.id === bankAccountId) ?? null;
  const bankBalance = accountBalance(tb, bankAccountId);

  // The batch on screen: the one you opened, else the newest open batch on this
  // cash book and this tab. Switching tab or cash book is therefore the whole
  // of the navigation, exactly as it is in Pastel.
  const forHere = (batches ?? []).filter(
    (b) => b.bankAccountId === bankAccountId && b.direction === direction);
  const openHere = forHere.find((b) => b.status === 'open') ?? null;
  const showing = batchId ?? openHere?.id ?? null;

  const openBatch = async (batchDate) => {
    setErr(null);
    try {
      const { cashBatch } = await api.post(`/api/entities/${entity.id}/cash-batches`,
        { bankAccountId, direction, batchDate });
      await loadBatches();
      setBatchId(cashBatch.id);
    } catch (e) { setErr(e.message); }
  };

  /**
   * A statement was just committed into one or two batches (routes/cashBook.js's
   * `POST .../cash-book/import`). Land on the payments batch, per the task
   * brief, and fall back to the receipts one for a statement that turned out
   * to be receipts-only — a real, tested case (`cashBookImport.js`'s
   * `summarise` handles a direction with no rows) that must still take the
   * bookkeeper somewhere rather than leaving them on the "no batch open"
   * screen after a commit that plainly worked.
   */
  const onStatementImported = (result) => {
    setErr(null);
    loadBatches();
    loadBalances();
    const landing = result.payments ? { key: 'payments', d: 'payment' } : { key: 'receipts', d: 'receipt' };
    const target = result[landing.key];
    if (target) {
      setDirection(landing.d);
      setBatchId(target.batchId);
    }
  };

  if (banks.length === 0 && accounts.length > 0) {
    return (
      <>
        <ReportHead entity={entity} title="Cash book" />
        <Empty>
          <p>No account on this company is marked as a bank account.</p>
          <p>
            A cash book opens against an account declared a bank — mark them on the chart of
            accounts. A practice usually has several: the cheque account, petty cash, the credit
            card, the trust account.
          </p>
        </Empty>
      </>
    );
  }

  const pageCss = PAGE_WIDTHS.find(([k]) => k === pageWidth)?.[2] ?? null;
  return (
    // The page breaks out of .main's standard cap; the margin keeps it centred.
    <div style={pageCss ? { width: pageCss, marginLeft: `calc(50% - ${pageCss} / 2)` } : undefined}>
      <ReportHead
        entity={entity}
        title="Cash book"
        sub="A bank statement, captured as a batch and checked before anyone posts it."
      />

      {err && <div className="error">{err}</div>}

      {/* ------------------------------------------------- the Pastel header */}
      <div className="cashbook__head">
        <label className="field cashbook__pick"><span>Cash book</span>
          <select
            value={bankAccountId}
            onChange={(e) => { setBankAccountId(e.target.value); setBatchId(null); }}
          >
            {banks.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
          </select>
        </label>

        <label className="field cashbook__pick"><span>Page width</span>
          <select
            value={pageWidth}
            onChange={(e) => { setPageWidth(e.target.value); savePageWidth(safeStorage(), e.target.value); }}
          >
            {PAGE_WIDTHS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>

        {/* Balance one of two: the cash book's OWN. The plan asked for this one
            and the screenshot shows a second; the status bar carries that. */}
        <div className="cashbook__balance">
          <div className="stat__label">Balance</div>
          <div className="stat__value">
            {bankBalance
              ? <>{money(bankBalance.unsigned)}<span className="cashbook__side">{bankBalance.side}</span></>
              : <span className="cashbook__unknown" title={tbRefused
                  ? 'a trial balance takes reports.ledger, which this role does not hold'
                  : 'this account has no movement in the current financial year'}>—</span>}
          </div>
          <div className="stat__sub">
            {bank ? `${bank.code} in the general ledger` : ''}
          </div>
        </div>
      </div>

      {/* --------------------------------------------------------- the tabs */}
      <div className="tabs" role="tablist" aria-label="Payments or receipts">
        {DIRECTIONS.map(([d, label]) => (
          <button
            key={d}
            role="tab"
            aria-selected={direction === d}
            className={`tab${direction === d ? ' tab--on' : ''}`}
            onClick={() => { setDirection(d); setBatchId(null); }}
          >
            {label}
          </button>
        ))}
      </div>

      {showing ? (
        <Batch
          key={showing}
          entity={entity}
          batchId={showing}
          canPost={canPost}
          accounts={accounts}
          taxCodes={taxCodes}
          counterparties={counterparties}
          periods={periods}
          tb={tb}
          onAccountCreated={loadAccounts}
          onChanged={() => { loadBatches(); loadBalances(); }}
          onLeave={() => { setBatchId(null); loadBatches(); }}
        />
      ) : (
        <NoBatch
          direction={direction}
          bank={bank}
          onOpen={openBatch}
          entity={entity}
          bankAccountId={bankAccountId}
          accounts={accounts}
          onImported={onStatementImported}
        />
      )}

      <div className="controls" style={{ marginTop: 22 }}>
        <button className="btn btn--quiet" onClick={() => setShowBatches((v) => !v)}>
          {showBatches ? 'Hide batches' : 'Batches'}
        </button>
        <span className="note" style={{ margin: 0 }}>
          Every batch on this company, whatever its cash book.
        </span>
      </div>

      {showBatches && (
        <BatchList
          batches={batches}
          onOpen={(b) => {
            setBankAccountId(b.bankAccountId);
            setDirection(b.direction);
            setBatchId(b.id);
            setShowBatches(false);
          }}
        />
      )}
    </div>
  );
}

/** No batch open on this cash book and this tab yet. */
function NoBatch({ direction, bank, onOpen, entity, bankAccountId, accounts, onImported }) {
  const today = new Date().toISOString().slice(0, 10);
  const [batchDate, setBatchDate] = useState(today);
  const [busy, setBusy] = useState(false);

  return (
    <div style={{ marginTop: 18 }}>
      <Empty>
        <p>
          No {direction === 'receipt' ? 'receipts' : 'payments'} batch is open
          {bank ? <> on {bank.code} {bank.name}</> : null}.
        </p>
        <p>{DIRECTION_SUB[direction]} Open one and start typing the statement, or import it below.</p>
      </Empty>
      <div className="controls">
        <label className="field"><span>Batch date</span>
          <input type="date" value={batchDate} onChange={(e) => setBatchDate(e.target.value)} />
        </label>
        <button
          className="btn"
          disabled={busy || !bank}
          onClick={async () => { setBusy(true); try { await onOpen(batchDate); } finally { setBusy(false); } }}
        >
          {busy ? 'Opening' : `Open a ${direction === 'receipt' ? 'receipts' : 'payments'} batch`}
        </button>
      </div>
      <div className="note">
        A draft batch burns no number and moves no account. It appears in no report until somebody
        posts it, and abandoning one leaves no gap in the sequence.
      </div>

      {/* Beside the manual "Open a batch" flow above, and sharing its batch
          date: the other way a batch gets its first lines. TWO SEPARATE
          controls rather than one file input that guesses the format — see
          `ImportPastelCashBook`'s own header comment for why. */}
      <ImportStatement
        entity={entity}
        bank={bank}
        bankAccountId={bankAccountId}
        accounts={accounts}
        batchDate={batchDate}
        onImported={onImported}
      />
      <ImportPastelCashBook
        entity={entity}
        bank={bank}
        bankAccountId={bankAccountId}
        batchDate={batchDate}
        onImported={onImported}
      />
    </div>
  );
}

/**
 * Read an ABSA .xlsx export, always preview it first, and only commit on an
 * explicit second click — routes/cashBook.js's two routes, task brief step 3.
 *
 * PREVIEW WRITES NOTHING (the route's own savepoint does that), so running it
 * twice, or three times while the bookkeeper swaps files, costs the entity
 * nothing. Nothing here re-derives what the preview or the commit shows: every
 * figure rendered comes straight off `summary`, exactly as api.js's header
 * requires — this component does no money arithmetic and no re-parsing of the
 * file, it only asks the two server routes and displays their answers.
 */
function ImportStatement({ entity, bank, bankAccountId, accounts, batchDate, onImported }) {
  const [fileName, setFileName] = useState(null);
  const [fileBase64, setFileBase64] = useState(null);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Holding accounts for lines no rule or history codes. Remembered per entity
  // (a screen preference, so localStorage only), and re-read once the chart
  // has loaded so an id that is no longer an option falls back to Automatic.
  const holdingOptions = useMemo(() => holdingAccountOptions(accounts), [accounts]);
  const [holding, setHolding] = useState(() => loadHoldingChoice(safeStorage(), entity.id, accounts));
  useEffect(() => {
    setHolding(loadHoldingChoice(safeStorage(), entity.id, accounts));
  }, [entity.id, accounts]);
  const chooseHolding = (key, id) => {
    const next = { ...holding, [key]: id };
    setHolding(next);
    saveHoldingChoice(safeStorage(), entity.id, next);
    // A preview made with other settings must not be the one that gets
    // committed — same reasoning as the bank-change reset below.
    setPreview(null);
  };

  // NoBatch is not remounted when the "Cash book" selector changes bank
  // account — only its props change — so without this, a preview run against
  // Bank A would still be sitting on screen after switching to Bank B, and
  // Import would then commit that same statement against the wrong bank
  // account. The file parses identically either way (the preview's rows and
  // balances do not depend on which account it posts to), so nothing here is
  // wasted work; it is purely a "you are about to import into a different
  // cash book than you checked" guard.
  useEffect(() => {
    setFileName(null); setFileBase64(null); setPreview(null); setErr(null);
  }, [bankAccountId]);

  // Picking a NEW file always clears any preview already on screen — the
  // guard against committing a preview that belonged to a file the bookkeeper
  // has since replaced. There is no "are you sure" here because there is
  // nothing yet to lose: nothing has been written until Import is pressed.
  const pickFile = (chosen) => {
    setErr(null); setPreview(null); setFileBase64(null);
    setFileName(chosen ? chosen.name : null);
    if (!chosen) return;
    const reader = new FileReader();
    reader.onload = () => {
      try { setFileBase64(base64FromDataUrl(reader.result)); } catch (e) { setErr(e.message); }
    };
    reader.onerror = () => setErr('Could not read that file.');
    reader.readAsDataURL(chosen);
  };

  const runPreview = async () => {
    if (!fileBase64 || !bankAccountId) return;
    setBusy(true); setErr(null); setPreview(null);
    try {
      const result = await api.post(`/api/entities/${entity.id}/cash-book/import/preview`,
        { bankAccountId, batchDate, fileBase64, ...holdingBody(holding) });
      setPreview(result);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const confirmImport = async () => {
    if (!fileBase64 || !bankAccountId) return;
    setBusy(true); setErr(null);
    try {
      const result = await api.post(`/api/entities/${entity.id}/cash-book/import`,
        { bankAccountId, batchDate, fileBase64, ...holdingBody(holding) });
      setFileName(null); setFileBase64(null); setPreview(null);
      onImported(result);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="cashbook-import" style={{ marginTop: 22 }}>
      <div className="controls">
        {/* The file input is left holding its own last selection — reading
            e.target.value back after a successful import would fight React,
            and a `key` reset would lose the chosen file on every unrelated
            render. `fileName` / `fileBase64` are what the rest of this
            component actually looks at, not the input's own DOM value. */}
        <label className="field"><span>Import a statement (.xlsx)</span>
          <input
            type="file"
            accept=".xlsx"
            disabled={busy || !bank}
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <button className="btn btn--quiet" disabled={busy || !fileBase64} onClick={runPreview}>
          {busy && !preview ? 'Reading…' : 'Preview'}
        </button>
      </div>

      <div className="controls">
        <label className="field"><span>Holding account for uncoded payments</span>
          <select value={holding.payments} disabled={busy}
            onChange={(e) => chooseHolding('payments', e.target.value)}>
            <option value="">Automatic (account 7300)</option>
            {holdingOptions.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
          </select>
        </label>
        <label className="field"><span>Holding account for uncoded receipts</span>
          <select value={holding.receipts} disabled={busy}
            onChange={(e) => chooseHolding('receipts', e.target.value)}>
            <option value="">Automatic (account 7400, else 4900)</option>
            {holdingOptions.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
          </select>
        </label>
      </div>
      <div className="note">Needed when your chart has no postable 7300 (payments) or 7400/4900 (receipts).</div>

      {!bank && (
        <div className="note">Pick a cash book above before importing a statement into it.</div>
      )}

      {err && <div className="error">{err}</div>}

      {preview && (
        <ImportPreview
          fileName={fileName}
          summary={preview.summary}
          busy={busy}
          onConfirm={confirmImport}
          onCancel={() => setPreview(null)}
        />
      )}
    </div>
  );
}

/**
 * What the statement would do, before anything is written.
 *
 * ⚠ THE UNVERIFIED BANNER IS THE WHOLE POINT OF THIS COMPONENT. It reuses
 * `.error` — this page's most visually forceful class, a solid left rule and
 * a tinted background, already used for a refused request — rather than the
 * quieter `.note--warn` strip a history suggestion gets elsewhere on this
 * screen (see this file's header, point 5). Those two are not the same kind
 * of fact: a history guess is a normal, expected part of every import, but a
 * statement whose running balance could not be checked is one the bookkeeper
 * must not mistake for one that passed, so it gets the LOUDER of this page's
 * two existing visual languages, not a new third one.
 *
 * THIS BANNER AND A REAL FAILED REQUEST MUST NOT LOOK IDENTICAL — round 1
 * review, 2026-09-19. Both this box and `ImportStatement`'s own `{err &&
 * <div className="error">...}` render as the same solid red block, and a
 * commit that fails right after an unverified preview would otherwise stack
 * two of them with nothing but the sentence to tell them apart — not safe to
 * ask an accountant reading this screen for the first time to notice under
 * pressure. The bolded "Unverified" label makes this ONE identifiable at a
 * glance without inventing a fourth visual language; the required sentence
 * itself is untouched, still its own text node, still verbatim.
 */
function ImportPreview({ fileName, summary, busy, onConfirm, onCancel }) {
  const unverified = statementIsUnverified(summary);
  const { rulesApplied, historyApplied, uncoded } = codingSplit(summary);
  return (
    <div style={{ marginTop: 14 }}>
      {unverified && (
        <div className="error">
          <strong style={{
            display: 'block', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4,
          }}
          >
            ⚠ Unverified
          </strong>
          {UNVERIFIED_BALANCE_MESSAGE}
        </div>
      )}

      <div className="stat-row">
        <div className="stat">
          <div className="stat__label">Rows kept</div>
          <div className="stat__value">{summary.rowsKept}</div>
          <div className="stat__sub">{summary.droppedZeroRows} dropped as zero-amount</div>
        </div>
        <div className="stat">
          <div className="stat__label">Period</div>
          <div className="stat__value" style={{ fontSize: 15 }}>{formatImportDateRange(summary)}</div>
          <div className="stat__sub">{fileName}</div>
        </div>
        <div className="stat">
          <div className="stat__label">Opening balance</div>
          <div className="stat__value"><Money value={summary.openingBalance} /></div>
        </div>
        <div className="stat">
          <div className="stat__label">Closing balance</div>
          <div className="stat__value"><Money value={summary.closingBalance} /></div>
        </div>
      </div>

      <div className="note">
        Coded on import: {rulesApplied} by an allocation rule, {historyApplied} guessed from this
        supplier's history, {uncoded} left on the entity's holding account uncoded. Nothing here has
        posted — a history guess and an uncoded line both still need a person's eye on the batch
        before it is posted.
      </div>

      <div className="controls">
        <button className="btn btn--quiet" disabled={busy} onClick={onCancel}>Cancel</button>
        <button className="btn" disabled={busy} onClick={onConfirm}>
          {busy ? 'Importing…' : 'Import this statement'}
        </button>
      </div>
    </div>
  );
}

/**
 * Read a Pastel Cash Book report (.xlsx) and preview-then-confirm it —
 * routes/cashBook.js's `import-pastel` pair, and structurally the same shell
 * as `ImportStatement` above: pick a file, read it to base64 with
 * `FileReader.readAsDataURL`, preview (writes nothing — its own savepoint),
 * confirm on an explicit second click. Nothing below re-parses the file or
 * re-derives a total; every figure rendered comes straight off `summary`.
 *
 * ⚠ A SECOND, SEPARATE CONTROL — NOT ONE FILE INPUT THAT GUESSES THE FORMAT.
 * Both exports are `.xlsx` (`pastelCashBook.js`'s own header: it reuses
 * `absaXlsx.js`'s zip reader rather than writing a second one), so the file
 * extension tells the browser nothing about which of the two this is, and
 * this component is deliberately not given any logic that opens the file and
 * inspects its rows to decide — that would be the file parsing this codebase
 * keeps in exactly one place (the two server-side readers), duplicated here
 * with nothing to keep the two in step.
 *
 * The other candidate design — one button, POST to the ABSA route, and on a
 * refusal retry the same file against this one — was rejected because the
 * two parsers do not fail symmetrically. `absaXlsx.js` has a single clean
 * "this is not one of ours" signal (`NOT_AN_ABSA_EXPORT`, thrown once, up
 * front, from the header row). `pastelCashBook.js` has no equivalent: an
 * ABSA file fed to it can fail as `PASTEL_CASHBOOK_EMPTY`,
 * `PASTEL_CASHBOOK_UNEXPECTED_ROW`, `PASTEL_CASHBOOK_BROKEN_PAIR` or
 * `PASTEL_CASHBOOK_BAD_DATE` depending on what its rows happen to look like —
 * there is no one code a "was this actually a Pastel file" retry could catch
 * before deciding to give up and show it as a real error. A guess-then-retry
 * button would therefore work cleanly in one direction and behave
 * unpredictably in the other, which is worse for a once-a-month user than two
 * labelled buttons that name the report they each expect.
 *
 * The two labels are also the two things Pastel itself calls these exports —
 * "a bank statement" and "a cash book report" — so picking the right one asks
 * the bookkeeper to recognise their own file, not to remember which button
 * this screen assigned to which format.
 */
function ImportPastelCashBook({ entity, bank, bankAccountId, batchDate, onImported }) {
  const [fileName, setFileName] = useState(null);
  const [fileBase64, setFileBase64] = useState(null);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Same reset-on-bank-switch guard as `ImportStatement` — see its comment.
  useEffect(() => {
    setFileName(null); setFileBase64(null); setPreview(null); setErr(null);
  }, [bankAccountId]);

  const pickFile = (chosen) => {
    setErr(null); setPreview(null); setFileBase64(null);
    setFileName(chosen ? chosen.name : null);
    if (!chosen) return;
    const reader = new FileReader();
    reader.onload = () => {
      try { setFileBase64(base64FromDataUrl(reader.result)); } catch (e) { setErr(e.message); }
    };
    reader.onerror = () => setErr('Could not read that file.');
    reader.readAsDataURL(chosen);
  };

  const runPreview = async () => {
    if (!fileBase64 || !bankAccountId) return;
    setBusy(true); setErr(null); setPreview(null);
    try {
      const result = await api.post(`/api/entities/${entity.id}/cash-book/import-pastel/preview`,
        { bankAccountId, batchDate, fileBase64 });
      setPreview(result);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const confirmImport = async () => {
    if (!fileBase64 || !bankAccountId) return;
    setBusy(true); setErr(null);
    try {
      const result = await api.post(`/api/entities/${entity.id}/cash-book/import-pastel`,
        { bankAccountId, batchDate, fileBase64 });
      setFileName(null); setFileBase64(null); setPreview(null);
      // The commit's own response is `{ payments, receipts, summary }` — the
      // same shape `ImportStatement`'s route answers with — so the parent's
      // `onStatementImported` (CashBook.jsx) needs no Pastel-specific branch
      // to land on the right batch.
      onImported(result);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="cashbook-import" style={{ marginTop: 22 }}>
      <div className="controls">
        <label className="field"><span>Import a Pastel cash book report (.xlsx)</span>
          <input
            type="file"
            accept=".xlsx"
            disabled={busy || !bank}
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <button className="btn btn--quiet" disabled={busy || !fileBase64} onClick={runPreview}>
          {busy && !preview ? 'Reading…' : 'Preview'}
        </button>
      </div>

      {!bank && (
        <div className="note">Pick a cash book above before importing a cash book report into it.</div>
      )}

      {err && <div className="error">{err}</div>}

      {preview && (
        <PastelImportPreview
          fileName={fileName}
          summary={preview.summary}
          busy={busy}
          onConfirm={confirmImport}
          onCancel={() => setPreview(null)}
        />
      )}
    </div>
  );
}

/**
 * What a Pastel cash book report would do, before anything is written.
 *
 * ⚠ A DIFFERENT SUMMARY SHAPE FROM `ImportPreview` ABOVE, ON PURPOSE — the
 * task brief's own flag. There is no three-tier proposal here (every line
 * arrives already coded — see `api/src/lib/cashBookImport.js`'s "SECOND
 * importer" section header), so there is no `rulesApplied` / `historyApplied`
 * / `uncoded` to show, and `codingSplit` is never called with this summary:
 * doing so would silently print "0 by a rule, 0 guessed, 0 uncoded", which
 * reads as a fact about this import rather than as what it actually is — a
 * question this summary shape has no answer to at all.
 *
 * ⚠ THE VAT RATE CHECK IS ITS OWN BLOCK, NOT A STAT TILE. `vatRateOutliers`
 * feeds VAT201, a statutory return — the task brief is explicit that burying
 * it "among six other stat tiles" is the one failure this preview must not
 * repeat. It gets the same loud `.error` treatment the unverified-balance
 * banner uses when it is non-zero (see `ImportPreview`'s own comment for why
 * that class is reserved for facts a bookkeeper must not mistake for "this
 * passed"), and the quieter `.ok` treatment when it is zero — zero is a real,
 * checked answer ("nothing outside the statutory rate"), not the absence of
 * one, so it is said plainly rather than left unshown.
 *
 * `balanceVerified` is always `false` here (`summarisePastelRows`'s own
 * comment: "a Pastel Cash Book report has no column this could ever be
 * computed from"), so the `⚠ Unverified` banner below always shows for this
 * importer — that is not a bug carried over from the ABSA path, it is the
 * documented, permanent state of this one.
 */
function PastelImportPreview({ fileName, summary, busy, onConfirm, onCancel }) {
  const unverified = statementIsUnverified(summary);
  const outliers = hasVatRateOutliers(summary);
  return (
    <div style={{ marginTop: 14 }}>
      {unverified && (
        <div className="error">
          <strong style={{
            display: 'block', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4,
          }}
          >
            ⚠ Unverified
          </strong>
          {UNVERIFIED_BALANCE_MESSAGE}
        </div>
      )}

      <div className={outliers ? 'error' : 'ok'} style={{ marginTop: unverified ? 10 : 0 }}>
        <strong style={{
          display: 'block', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4,
        }}
        >
          {outliers ? '⚠ VAT rate check' : 'VAT rate check'}
        </strong>
        {vatOutlierMessage(summary)}
      </div>

      <div className="stat-row" style={{ marginTop: 14 }}>
        <div className="stat">
          <div className="stat__label">Rows kept</div>
          <div className="stat__value">{summary.rowsKept}</div>
        </div>
        <div className="stat">
          <div className="stat__label">Period</div>
          <div className="stat__value" style={{ fontSize: 15 }}>{formatImportDateRange(summary)}</div>
          <div className="stat__sub">{fileName}</div>
        </div>
        <div className="stat">
          <div className="stat__label">Payments</div>
          <div className="stat__value"><Money value={summary.paymentsIncl} /></div>
        </div>
        <div className="stat">
          <div className="stat__label">Receipts</div>
          <div className="stat__value"><Money value={summary.receiptsIncl} /></div>
        </div>
      </div>

      <div className="controls">
        <button className="btn btn--quiet" disabled={busy} onClick={onCancel}>Cancel</button>
        <button className="btn" disabled={busy} onClick={onConfirm}>
          {busy ? 'Importing…' : 'Import this cash book'}
        </button>
      </div>
    </div>
  );
}

/** Every batch on the company, newest first — Pastel's `Batch…`. */
function BatchList({ batches, onOpen }) {
  if (batches === null) return <div className="empty">Loading…</div>;
  if (batches.length === 0) return <Empty><p>No batches yet.</p></Empty>;
  return (
    <div className="scroll-x">
      <table className="ledger">
        <thead>
          <tr>
            <th style={{ width: 90 }}>Number</th>
            <th style={{ width: 110 }}>Date</th>
            <th>Cash book</th>
            <th style={{ width: 100 }}>Tab</th>
            <th style={{ width: 170 }}>Status</th>
            <th className="num" style={{ width: 70 }}>Lines</th>
            <th className="num" style={{ width: 140 }}>Total</th>
            <th style={{ width: 80 }} />
          </tr>
        </thead>
        <tbody>
          {batches.map((b) => (
            <tr key={b.id}>
              {/* NULL UNTIL POST, and null for ever on an abandoned batch:
                  decision 6, not a missing value. A draft is shown as a draft. */}
              <td className="code figure">{b.number ?? '—'}</td>
              <td className="figure">{b.batchDate}</td>
              <td><span className="code figure">{b.bankCode}</span> {b.bankName}</td>
              <td>{b.direction === 'receipt' ? 'Receipts' : 'Payments'}</td>
              <td>{STATUS_LABEL[b.status] ?? b.status}</td>
              <td className="num figure">{b.lineCount ?? '—'}</td>
              <td className="num"><Money value={b.total} /></td>
              <td className="num">
                <button className="icon-btn" onClick={() => onOpen(b)}>Open</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One batch: the grid, the buttons, the running total and the status bar.
 *
 * EVERY WRITE COMES BACK WITH THE TOTALS. The API sends them on the add, the
 * change and the delete precisely so the figure on the screen is the figure the
 * post will be authorised against — one round trip, and no second call whose
 * answer could belong to a different version of the lines above it.
 */
function Batch({
  entity, batchId, canPost, accounts, taxCodes, counterparties, periods, tb, onAccountCreated, onChanged, onLeave,
}) {
  const [batch, setBatch] = useState(null);
  const [lines, setLines] = useState([]);
  const [totals, setTotals] = useState(null);
  const [drafts, setDrafts] = useState({});          // line id -> the row being edited
  const [draft, setDraft] = useState(blankRow());     // the row being typed at the bottom
  const [focused, setFocused] = useState(null);       // line id, or 'draft'
  const [busy, setBusy] = useState(false);
  // Which captured line's blur-commit is in flight, if any. Kept separate from
  // `busy` (which covers batch-level writes: add/remove/post/abandon) so that
  // saving one row's edit does not disable every OTHER row in the grid — a
  // bookkeeper clicking into the next line while the previous one's PATCH is
  // still on the wire would otherwise have that click swallowed by a disabled
  // cell and have to click again.
  const [savingLineId, setSavingLineId] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [posted, setPosted] = useState(null);
  const [confirmAbandon, setConfirmAbandon] = useState(false);
  // The last answer from `/proposal` and the query string it answers — never
  // the proposal alone. `proposalFor` hands it back only while the row it was
  // fetched for is still the row being typed, so an answer for a description
  // that has since been edited can neither be shown nor applied.
  const [held, setHeld] = useState(null);

  // The Match <F3> seam. Keyed by line id, exactly as the post route takes it;
  // nothing fills it in yet, so every subledger line settles on account. See
  // allocationsForPost in lib/cashBookGrid.js for what a picker has to feed.
  const [allocations] = useState({});

  const draftDateRef = useRef(null);

  // THE SORT IS DISPLAY ONLY AND PER VIEWER. `lines` stays in capture order and
  // is what "the last line", the next reference and the status bar read; only
  // the <tbody> map below walks `shownLines`. The order is recomputed when the
  // sort changes or the SET of lines changes (add, remove, reload) — NOT when a
  // value is committed — so a row edited under a sort stays where it is until
  // the header is clicked again, and never jumps out from under the cursor.
  // Rows are keyed by id either way, so nothing remounts and focus is kept.
  const [sort, setSort] = useState(null);             // { key, dir } or null = capture order
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const lineSet = lines.map((l) => l.id).join(',');
  // Only the ORDER (a list of ids) is memoised. The line objects are looked up
  // fresh on every render, so a value the server changed — an edit committed, or
  // a generated reference renumbered by a reload — is shown even though the
  // order, deliberately, did not move.
  const shownOrder = useMemo(
    () => (sort
      ? sortLines(linesRef.current, { accounts, periods, taxCodes, counterparties }, sort.key, sort.dir)
        .map((l) => l.id)
      : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sort, lineSet, accounts, periods, taxCodes, counterparties]);
  const orderedLines = shownOrder
    ? shownOrder.map((id) => lines.find((l) => l.id === id)).filter(Boolean)
    : lines;
  // The search only hides rows from view; nothing is sent or changed by it.
  const [search, setSearch] = useState('');
  const shownLines = searchLines(orderedLines, { accounts, counterparties }, search);

  const cw = useColumnWidths(batch?.status === 'open');

  // GENERATED REFERENCES. `refPreview` is the last answer from next-reference for
  // the draft row's date. It is a PREVIEW — it reserves nothing, and the server
  // assigns the real number on save — so it is only ever shown as a hint.
  // `autoRef` mirrors "is that answer GENERATED" for the handlers below, which
  // run from closures that would otherwise read a stale one.
  const [refPreview, setRefPreview] = useState(null);
  const autoRef = useRef(false);
  const previewSeq = useRef(0);

  /**
   * EVERY WRITE ON THIS BATCH RUNS ONE AT A TIME AND IN ORDER.
   *
   * A grid that commits on blur makes the last keystroke and the Post button
   * two things happening at once: mousedown blurs the row (which saves it) and
   * the click posts, and unserialised those two requests race. The batch would
   * then be posted without the line the bookkeeper had just typed — and they
   * would have no way of knowing, because the row was on the screen.
   *
   * A promise chain rather than a `busy` flag, because a flag can only refuse
   * the second write; what is wanted is for it to HAPPEN, after the first.
   */
  const chain = useRef(Promise.resolve());
  const serial = (fn) => {
    const next = chain.current.then(fn);
    chain.current = next.catch(() => {});
    return next;
  };

  // Set synchronously, so a blur that follows an Enter on the same row does not
  // send the row twice. React has not re-rendered by then and the draft still
  // holds what was just sent.
  const savingDraft = useRef(false);

  const draftRef = useRef(null);
  draftRef.current = draft;

  /**
   * Ask the server what the next generated reference would be for this date.
   * Sequenced, so a slow answer for an older date can never replace a newer one.
   * A failed call is the same as "no prefix": the grid keeps its old behaviour.
   * If the answer flips the mode, a Reference field the bookkeeper has not
   * typed in is moved with it (referenceOnModeChange never overwrites typing).
   */
  const refreshPreview = useCallback(async (date) => {
    const n = ++previewSeq.current;
    let p = null;
    try { p = await api.get(nextReferenceUrl(batchId, date)); } catch { p = null; }
    if (n !== previewSeq.current) return;
    const wasAuto = autoRef.current;
    const nowAuto = isAutoPreview(p);
    autoRef.current = nowAuto;
    setRefPreview(p);
    if (wasAuto !== nowAuto) {
      setDraft((r) => ({
        ...r,
        reference: referenceOnModeChange({
          current: r.reference, seed: nextReferenceAfter(linesRef.current), wasAuto, nowAuto }),
      }));
    }
  }, [batchId]);

  const load = useCallback(() => api.get(`/api/cash-batches/${batchId}`)
    .then((d) => {
      setBatch(d.cashBatch);
      // A reload can follow a renumbering: an edit row that still holds a line's
      // OLD reference is moved to the new one, or it would be sent back as typed.
      setDrafts((ds) => rebaseDraftReferences(ds, linesRef.current, d.lines));
      setLines(d.lines);
      setTotals(d.totals);
      // The FIRST draft row of a session picks up where the batch stopped: a
      // batch half-captured yesterday should not make somebody read the last
      // row and retype its reference plus one. Only when there is no draft yet
      // — reloading mid-capture must never overwrite what is being typed.
      setDraft((r) => (r.transactionDate ? r : blankRow({
        transactionDate: d.cashBatch.batchDate,
        reference: nextReferenceAfter(d.lines),
      })));
    })
    .catch((e) => setErr(e.message)), [batchId]);

  useEffect(() => { load(); }, [load]);

  const isOpen = batch?.status === 'open';

  // The preview follows the draft row's DATE (the month is part of the number).
  // Debounced, and skipped while the date is half-typed — never per keystroke.
  const draftDate = draft.transactionDate;
  const dateAskable = draftDate === '' || Boolean(previewDate(draftDate));
  useEffect(() => {
    if (!isOpen || !dateAskable) return undefined;
    const timer = setTimeout(() => { refreshPreview(draftDate); }, 300);
    return () => clearTimeout(timer);
  }, [isOpen, dateAskable, draftDate, refreshPreview]);
  const refHint = referenceHint(refPreview);

  /**
   * WHAT WOULD THIS ROW BE CODED TO — asked of the server, and only the server.
   *
   * The badge below is the one SlipReview.jsx shows, and it is fed by the same
   * `matchRule`/`proposeFromHistory` pair through `proposeAllocation`, so the
   * two screens cannot disagree about what would have fired. Nothing about
   * matching is reimplemented in the browser; that drifted once already on the
   * slip screen and the route exists because of it.
   *
   * Debounced, because this fires on a description as it is typed. The query
   * string is the effect's whole dependency AND the identity of the answer —
   * see `proposalFor` — so a keystroke that changes nothing worth asking about
   * costs no request.
   */
  const query = isOpen ? proposalQuery(draft) : null;
  useEffect(() => {
    if (!query) { setHeld(null); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => {
      api.get(`/api/cash-batches/${batchId}/proposal?${query}`)
        .then((p) => { if (!cancelled) setHeld({ query, proposal: p }); })
        // A suggestion that cannot be fetched is a suggestion not shown. It is
        // an aid to typing, never a gate on it, so a failure here must not put
        // an error in front of somebody who is mid-line.
        .catch(() => { if (!cancelled) setHeld(null); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [batchId, query]);

  const proposal = proposalFor(draft, held);

  const byLine = useMemo(() => unpostableByLine(totals?.unpostable), [totals]);
  const batchReasons = batchLevelReasons(totals?.unpostable);
  const rowFor = (line) => drafts[line.id] ?? rowFromLine(line);

  const setRow = (line, patch) =>
    setDrafts((d) => ({ ...d, [line.id]: { ...(d[line.id] ?? rowFromLine(line)), ...patch } }));

  const after = (payload) => {
    setTotals(payload.totals);
    onChanged();
  };

  /** Save the row being typed. Pastel's Enter, and the bottom of the grid. */
  const addLineNow = async () => {
    if (savingDraft.current) return;
    // The proposal travels into both calls or into neither: a row whose account
    // a RULE is about to supply is not a row missing an account, and the same
    // flag that stops it being refused here is what asks for it on the wire.
    const auto = autoAllocates(draft, proposal);
    const problem = rowProblem(draft, proposal);
    if (problem) { setErr(problem); return; }
    savingDraft.current = true;
    setErr(null); setMsg(null); setBusy(true);
    try {
      const payload = await api.post(`/api/cash-batches/${batchId}/lines`,
        lineBody(draft, { autoAllocate: auto }));
      setLines((ls) => [...ls, payload.line]);
      after(payload);
      // The date, the kind, the VAT code and the REFERENCE carry over: a
      // statement is typed in runs of similar lines, and re-choosing all of
      // them every time is the difference between a screen you can work in and
      // one you cannot.
      //
      // The reference is the one that MOVES — `P27-0206` → `P27-0207`, the
      // owner's own column (design spec, finding 2). It is incremented off the
      // line that was just SAVED, so a reference the bookkeeper typed over is
      // what the next row counts on from; the original seed is never
      // remembered. It stays an ordinary editable field, and this is not and
      // must never become `next_counter` — that is the gapless batch number,
      // allocated at post time, and it is nobody's to guess.
      setDraft(blankRow({
        transactionDate: draft.transactionDate,
        contraKind: draft.contraKind,
        taxCodeId: draft.contraKind === 'ledger' ? draft.taxCodeId : '',
        // With a prefix the next row is EMPTY (the hint shows the number the
        // server will give it); without one, the old client-side count.
        reference: referenceAfterSave(autoRef.current, payload.line.reference),
      }));
      refreshPreview(draft.transactionDate);
      // `appliedRuleId` is the API's answer to "did a rule code this", and it
      // is null unless one actually fired on this call. Said out loud, because
      // a line that coded itself is a line the bookkeeper never chose an
      // account for and should be told about.
      setMsg(payload.line.appliedRuleId
        ? `Line ${payload.line.lineNo} captured and coded by a rule.`
        : `Line ${payload.line.lineNo} captured.`);
      draftDateRef.current?.focus();
    } catch (e) { setErr(e.message); } finally { setBusy(false); savingDraft.current = false; }
  };

  /** Commit an edit to a captured line — only what actually changed. */
  const commitLineNow = async (line) => {
    const row = drafts[line.id];
    if (!row) return;
    const patch = patchBody(row, line);
    if (Object.keys(patch).length === 0) {
      setDrafts((d) => { const n = { ...d }; delete n[line.id]; return n; });
      return;
    }
    const problem = rowProblem(row);
    if (problem) { setErr(problem); return; }
    setErr(null); setMsg(null); setSavingLineId(line.id);
    try {
      const payload = await api.patch(`/api/cash-batches/${batchId}/lines/${line.id}`, patch);
      setLines((ls) => ls.map((l) => (l.id === line.id ? payload.line : l)));
      after(payload);
      setDrafts((d) => { const n = { ...d }; delete n[line.id]; return n; });
      setMsg(`Line ${payload.line.lineNo} changed.`);
      // Re-dating a draft line can renumber others (the month is in the number
      // and gaps close), so show the server's numbers rather than ours.
      if (patch.transactionDate !== undefined) {
        await load();
        refreshPreview(draftRef.current?.transactionDate);
      }
    } catch (e) { setErr(e.message); } finally { setSavingLineId(null); }
  };

  /** Pastel's `Delete <Ctrl-D>`. The remaining lines keep their numbers. */
  const removeLineNow = async (line) => {
    setErr(null); setMsg(null); setBusy(true);
    try {
      const payload = await api.del(`/api/cash-batches/${batchId}/lines/${line.id}`);
      setLines((ls) => ls.filter((l) => l.id !== line.id));
      after(payload);
      setMsg(`Line ${line.lineNo} removed. The rows above keep their numbers.`);
      // Higher draft lines may have been renumbered down to close the gap.
      await load();
      refreshPreview(draftRef.current?.transactionDate);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  /** Pastel's `Update…` — the whole point of the module. */
  const postNow = async () => {
    setErr(null); setMsg(null); setBusy(true);
    try {
      const result = await api.post(`/api/cash-batches/${batchId}/post`,
        { allocations: allocationsForPost(allocations) });
      setPosted(result);
      setTotals(result.totals);
      await load();
      onChanged();
    } catch (e) {
      // 422 BATCH_UNPOSTABLE carries the list, and the list is the point: it
      // names the rows to fix. Showing it against those rows is the whole
      // reason it travels per line instead of as a verdict.
      if (e.code === 'BATCH_UNPOSTABLE' && e.payload?.totals) setTotals(e.payload.totals);
      setErr(e.message);
    } finally { setBusy(false); }
  };

  const recleanNow = async () => {
    setErr(null); setBusy(true);
    try {
      const result = await api.post(`/api/cash-batches/${batchId}/reclean-descriptions`, {});
      await load();
      onChanged();
      setMsg(recleanMessage(result));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const abandonNow = async () => {
    setErr(null); setBusy(true);
    try {
      await api.post(`/api/cash-batches/${batchId}/abandon`, {});
      await load();   // an abandoned batch's numbers are released, so other drafts renumber
      onChanged();
      setMsg('This batch has been abandoned. It burned no number and moved nothing.');
    } catch (e) { setErr(e.message); } finally { setBusy(false); setConfirmAbandon(false); }
  };

  // Everything that writes goes through the chain, so the order the bookkeeper
  // did things in is the order the server sees them in.
  const addLine = () => serial(addLineNow);
  const commitLine = (line) => serial(() => commitLineNow(line));
  const removeLine = (line) => serial(() => removeLineNow(line));
  const post = () => serial(postNow);
  const abandon = () => serial(abandonNow);
  const reclean = () => serial(recleanNow);

  /**
   * The keyboard, because the owner types hundreds of lines.
   *
   * Ctrl-I and Ctrl-D are Pastel's own, on the same keys, and both are taken
   * off the browser (Ctrl-D bookmarks a page otherwise). F3 is Pastel's Match
   * and is greyed there too — here it says what it would do and where the seam
   * is, rather than silently doing nothing.
   */
  const onKeyDown = (e) => {
    if (e.key === 'F3') {
      e.preventDefault();
      const line = lines.find((l) => l.id === focused);
      setMsg(isMatchable(line)
        ? `Line ${line.lineNo} will settle ON ACCOUNT. Matching it against open invoices is the `
          + 'allocation picker, which is not built yet — the post already carries allocations.'
        : 'Match applies to a customer or supplier line — a G line has no invoice to match.');
      return;
    }
    if (!isOpen || !e.ctrlKey) return;
    if (e.key === 'i' || e.key === 'I') {
      e.preventDefault();
      draftDateRef.current?.focus();
    }
    if (e.key === 'd' || e.key === 'D') {
      const line = lines.find((l) => l.id === focused);
      if (!line) return;
      e.preventDefault();
      removeLine(line);
    }
  };

  if (!batch) {
    return (
      <>
        {err && <div className="error">{err}</div>}
        <div className="empty">{err ? '' : 'Loading…'}</div>
      </>
    );
  }

  // The status bar follows the cursor, and falls back to the last line captured
  // when nothing has focus — a bar that is blank until you click something is a
  // bar nobody learns to read.
  const cursorLine = lines.find((l) => l.id === focused) ?? null;
  // An untouched draft row is not a place to read a balance from, so the bar
  // rests on the last line captured until the row you are typing has something
  // in it. That is also what it shows before anything has been clicked at all.
  const onDraft = focused === 'draft' && !isRowEmpty(draft);
  const restingLine = lines.length > 0 ? lines[lines.length - 1] : null;
  const shownRow = onDraft ? draft : (cursorLine ? rowFor(cursorLine)
    : (restingLine ? rowFor(restingLine) : null));
  const contraBalance = accountBalance(tb, shownRow?.contraAccountId);

  // SlipReview.jsx's own two labels, so the badge below reads identically on
  // both screens.
  const accountLabel = (id) => {
    const a = accounts.find((x) => x.id === id);
    return a ? `${a.code} — ${a.name}` : id;
  };
  const taxLabel = (id) => {
    const t = taxCodes.find((x) => x.id === id);
    return t ? `${t.code} — ${t.name}` : null;
  };
  const focusedLine = onDraft
    ? { reference: draft.reference, amountIncl: draft.amountIncl }
    : cursorLine ?? restingLine;

  const postable = isPostable(totals);
  // Buttons that act on the batch as a whole still wait out ANY in-flight
  // write, line-commit included — only the grid's other rows are freed up.
  const anyBusy = busy || savingLineId !== null;

  return (
    <div onKeyDown={onKeyDown}>
      <div className="cashbook__bar">
        <span className="cashbook__batch">
          {batch.number ? `Batch ${batch.number}` : 'Draft batch'}
        </span>
        <span className="cashbook__meta">
          {batch.bankCode} {batch.bankName} · {batch.batchDate} ·{' '}
          {STATUS_LABEL[batch.status] ?? batch.status}
        </span>
        <span className="cashbook__spacer" />
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Search name or amount" aria-label="Search this batch"
               title="Shows only the lines whose description, reference, account, name or amount contain what you type" />
        {/* Where Pastel puts it: top right, beside the batch it closes. */}
        <button className="btn btn--quiet" disabled={!cw.anySaved} onClick={cw.resetAll}
                title="Put every column of the grid back to its standard width">
          Reset column widths
        </button>
        <button className="btn btn--quiet" onClick={onLeave}>Close</button>
      </div>

      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      {batchReasons.length > 0 && isOpen && lines.length > 0 && (
        <div className="note note--warn">
          {batchReasons.map((u) => u.reason).join(' · ')}
        </div>
      )}

      {search.trim() && (
        <div className="note">
          Showing {shownLines.length} of {lines.length} lines matching "{search.trim()}".
        </div>
      )}

      <div className="scroll-x scroll-x--page">
        <table className="ledger ledger--grid" ref={cw.tableRef} style={cw.tableStyle}>
          {/* THE WIDTHS LIVE ON THE <col>s, and the table is table-layout: fixed.
              A drag writes the <col>'s style directly and commits state on
              release, so no row re-renders while the pointer moves. The
              numbers are the old inline header widths, now only defaults. */}
          <colgroup>
            {cw.columns.map(([key]) => (
              <col key={key} ref={cw.colRef(key)}
                   style={cw.widths[key] === null ? undefined : { width: cw.widths[key] }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th />
              <SortTh k="per" sort={sort} onSort={setSort} cw={cw} title="The accounting period the line falls in">Per</SortTh>
              <SortTh k="date" sort={sort} onSort={setSort} cw={cw}>Date</SortTh>
              <SortTh k="gcs" sort={sort} onSort={setSort} cw={cw} title="General ledger, Customer or Supplier">GCS</SortTh>
              <SortTh k="account" sort={sort} onSort={setSort} cw={cw}>Account</SortTh>
              <SortTh k="reconciled" sort={sort} onSort={setSort} cw={cw}
                      title="Reconciled against the bank statement. Bank reconciliation is its own milestone; this column is filled in there and is not editable here.">
                R
              </SortTh>
              {/* Description takes whatever is left, and deliberately: it is the
                  narration the general ledger will carry and the column the
                  bookkeeper actually reads back against the statement. */}
              <SortTh k="reference" sort={sort} onSort={setSort} cw={cw}>Reference</SortTh>
              <SortTh k="description" sort={sort} onSort={setSort} cw={cw}>Description</SortTh>
              <SortTh k="tax" sort={sort} onSort={setSort} cw={cw}>Tax</SortTh>
              <SortTh k="excl" sort={sort} onSort={setSort} cw={cw} className="num col-debit">Bank<br />Exclusive</SortTh>
              <SortTh k="incl" sort={sort} onSort={setSort} cw={cw} className="num col-debit">Bank<br />Inclusive</SortTh>
              {isOpen && <th />}
            </tr>
          </thead>

          <tbody>
            {shownLines.map((line) => (
              <LineRow
                key={line.id}
                line={line}
                row={rowFor(line)}
                dirty={Boolean(drafts[line.id])}
                refusals={byLine.get(line.lineNo) ?? []}
                editable={isOpen}
                batchPosted={batch.status === 'posted'}
                busy={busy || savingLineId === line.id}
                accounts={accounts}
                tb={tb}
                entity={entity}
                onAccountCreated={onAccountCreated}
                taxCodes={taxCodes}
                counterparties={counterparties}
                periods={periods}
                bankAccountId={batch.bankAccountId}
                direction={batch.direction}
                allocations={allocations}
                onFocus={() => setFocused(line.id)}
                onChange={(patch) => setRow(line, patch)}
                onCommit={() => commitLine(line)}
                onRemove={() => removeLine(line)}
              />
            ))}

            {isOpen && (
              <DraftRow
                row={draft}
                busy={busy}
                accounts={accounts}
                tb={tb}
                entity={entity}
                onAccountCreated={onAccountCreated}
                taxCodes={taxCodes}
                counterparties={counterparties}
                periods={periods}
                bankAccountId={batch.bankAccountId}
                direction={batch.direction}
                proposal={proposal}
                refHint={refHint}
                dateRef={draftDateRef}
                onFocus={() => setFocused('draft')}
                onChange={(patch) => setDraft((r) => ({ ...r, ...patch }))}
                onSave={addLine}
              />
            )}
          </tbody>

          {/* THE RUNNING TOTAL, and what stands in for Pastel's Batch Total
              field. Every figure is the server's: `totals` came back with the
              write that changed it. */}
          {totals && (
            <tfoot>
              <tr>
                <td colSpan={9}>
                  {totals.lineCount} line{totals.lineCount === 1 ? '' : 's'} captured
                  {totals.vatCents !== '0' && <> · VAT {money(totals.vat)}</>}
                </td>
                <td className="num"><Money value={totals.excl} /></td>
                <td className="num"><Money value={totals.incl} /></td>
                {isOpen && <td />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ------------------------------------------------ the coding suggestion
          SlipReview.jsx's badge, class for class and sentence for sentence: a
          confirmed rule is `ok`, a history guess is `note note--warn` and says
          in the same words that it is NOT a confirmed rule. The one difference
          is the button, and it is the difference that matters — a slip is
          allocated by a human pressing Save, but this grid commits a row on
          blur, so a history suggestion that seeded the account field would BE
          an auto-posting from history the moment somebody pressed Enter. It
          therefore reaches the row only through `acceptProposal`, and only
          because somebody pressed this. */}
      {query && proposal && (
        proposal.source === 'rule' ? (
          <div className="ok">
            <strong>Rule:</strong> {accountLabel(proposal.accountId)}
            {taxLabel(proposal.taxCodeId) ? ` · ${taxLabel(proposal.taxCodeId)}` : ''}
            {draft.contraAccountId
              ? ' — you have chosen an account yourself, and that is what will be saved.'
              : ' — this line will be coded automatically when you save it.'}
          </div>
        ) : proposal.source === 'history' ? (
          <div className="note note--warn">
            <strong>History suggestion</strong> ({proposal.basis}) — not a confirmed rule:{' '}
            {accountLabel(proposal.accountId)}
            {taxLabel(proposal.taxCodeId) ? ` · ${taxLabel(proposal.taxCodeId)}` : ''}
            <button className="btn btn--quiet" style={{ marginLeft: 8 }} disabled={anyBusy}
                    onClick={() => setDraft((r) => ({ ...r, ...acceptProposal(r, proposal) }))}>
              Use this
            </button>
          </div>
        ) : (
          <div className="note">
            No rule for this description yet — choose the account yourself.
          </div>
        )
      )}

      {/* ------------------------------------------------- Pastel's status bar
          Two balances, not one: the cash book's own is in the header above, and
          this is the CONTRA account the cursor is sitting on. The plan asked
          for the first; the screenshot shows both, and a bookkeeper coding a
          line to 3200 wants to see what 3200 already holds. */}
      <div className="statusbar">
        <span className="statusbar__cell figure">{focusLabel(focusedLine, batch.direction)}</span>
        <span className="statusbar__cell statusbar__name">
          {contraBalance ? `${contraBalance.code} ${contraBalance.name}` : ''}
        </span>
        <span className="statusbar__cell num figure">
          {contraBalance
            ? <>{money(contraBalance.unsigned)}<span className="cashbook__side">{contraBalance.side}</span></>
            : ''}
        </span>
      </div>

      {/* --------------------------------------------------------- the buttons */}
      {isOpen && (
        <div className="controls cashbook__buttons">
          <button className="btn btn--quiet" disabled={anyBusy}
                  onClick={() => draftDateRef.current?.focus()}>
            Insert &lt;Ctrl-I&gt;
          </button>
          <button className="btn btn--quiet" disabled={anyBusy || !lines.find((l) => l.id === focused)}
                  onClick={() => removeLine(lines.find((l) => l.id === focused))}>
            Delete &lt;Ctrl-D&gt;
          </button>
          {/* GREYED, exactly as it is in the owner's own screenshot. No task in
              the milestone owns the allocation picker; a customer or supplier
              line settles on account, which is the honest default. */}
          <button className="btn btn--quiet" disabled
                  title="Match a subledger line against open invoices. Not built: every customer or supplier line settles on account, which the age analysis reports as an unallocated credit rather than netting it off the oldest invoice.">
            Match &lt;F3&gt;
          </button>

          <button className="btn btn--quiet" disabled={anyBusy || lines.length === 0} onClick={reclean}
                  title="Applies your clean-up rules again to this draft's descriptions; coding and amounts are not changed.">
            Re-clean descriptions
          </button>

          <span className="cashbook__spacer" />

          {confirmAbandon ? (
            <>
              <button className="btn btn--quiet" disabled={anyBusy} onClick={abandon}>
                Really abandon
              </button>
              <button className="btn btn--quiet" onClick={() => setConfirmAbandon(false)}>Keep it</button>
            </>
          ) : (
            <button className="btn btn--quiet" disabled={anyBusy}
                    onClick={() => setConfirmAbandon(true)}>
              Abandon
            </button>
          )}

          {canPost && (
            <button className="btn" disabled={anyBusy || !postable} onClick={post}
                    title={postable ? 'Post every line as its own journal entry'
                      : 'Some lines cannot be posted yet — see the rows marked below'}>
              {busy ? 'Posting' : 'Post the batch'}
            </button>
          )}
        </div>
      )}

      {isOpen && !canPost && lines.length > 0 && (
        <div className="note">
          You may capture this batch and you may not post it. Somebody holding <code>cashbook.post</code>{' '}
          checks the total against the statement and commits it — which is the entire reason the
          batch exists as a draft.
        </div>
      )}

      {isOpen && !postable && lines.length > 0 && (
        <div className="note note--warn">
          <strong>
            {(totals?.unpostable ?? []).length} thing
            {(totals?.unpostable ?? []).length === 1 ? '' : 's'} to fix before this batch posts.
          </strong>{' '}
          Each one is marked against its row.
        </div>
      )}

      {totals && totals.lineCount > 0 && <TaxSplit totals={totals} />}

      {posted && <Posted result={posted} />}

      <div className="note">
        Each line posts as its own journal entry, dated by its own date, so the general ledger reads
        line by line and the VAT return can be reconciled to it. The batch total is exactly the
        movement this batch causes on {batch.bankCode} — that is the whole invariant, and it is why
        the total is checked before anything is posted.
      </div>
    </div>
  );
}

/**
 * One captured line, editable in place.
 *
 * A row commits when it LOSES FOCUS, which is how a grid is typed: across the
 * row and on to the next. `patchBody` sends only what changed, so tabbing
 * through a row you did not alter costs no request at all.
 */
function LineRow({
  line, row, dirty, refusals, editable, batchPosted, busy, accounts, tb, entity, onAccountCreated, taxCodes, counterparties,
  periods, bankAccountId, direction, allocations, onFocus, onChange, onCommit, onRemove,
}) {
  const period = periodById(periods, line.periodId) ?? periodForDate(periods, row.transactionDate);
  const refused = refusals.length > 0;

  const leaveRow = (e) => {
    // Only when focus has actually left the row — moving between two cells of
    // the same line is not the end of the edit.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    onCommit();
  };

  return (
    <tr
      className={`${refused ? 'row--refused' : ''}${dirty ? ' row--dirty' : ''}`}
      onFocus={onFocus}
      onBlur={editable ? leaveRow : undefined}
    >
      <td className="figure lineno">{line.lineNo}</td>
      <td className="figure code" title={period ? `${period.label} · ${period.status}` : undefined}>
        {periodTag(period)}
      </td>
      {editable ? (
        <>
          <td>
            <input type="date" value={row.transactionDate} disabled={busy}
                   onChange={(e) => onChange({ transactionDate: e.target.value })} />
          </td>
          <td>
            <select value={row.contraKind} disabled={busy}
                    onChange={(e) => onChange({ contraKind: e.target.value })}>
              {CONTRA_KINDS.map(([k, letter, label]) => (
                <option key={k} value={k} title={label}>{letter}</option>
              ))}
            </select>
          </td>
          <td>
            <ContraPicker row={row} accounts={accounts} tb={tb} entity={entity}
                          onAccountCreated={onAccountCreated} counterparties={counterparties}
                          bankAccountId={bankAccountId} busy={busy} onChange={onChange} />
          </td>
          <td className="figure code" title="Bank reconciliation fills this in; it is not typed here.">
            {line.isReconciled ? 'Y' : 'N'}
          </td>
          <td>
            <input value={row.reference} disabled={busy} maxLength={REFERENCE_MAX}
                   onChange={(e) => onChange({ reference: e.target.value })} />
            {line.referenceGenerated && <AutoMark />}
          </td>
          <td>
            <div className="desc-cell">
              <input value={row.description} disabled={busy}
                     onChange={(e) => onChange({ description: e.target.value })} />
              {line.descriptionOriginal != null && <OriginalDescription text={line.descriptionOriginal} />}
            </div>
            {refusals.map((u) => (
              <div key={u.code} className="row-refusal">{u.reason}</div>
            ))}
            {isMatchable(line) && (
              <div className="row-note">{settlementNote(line, allocations)}</div>
            )}
          </td>
          <td>
            <TaxPicker row={row} taxCodes={taxCodes} direction={direction}
                       busy={busy} onChange={onChange} />
          </td>
          <td className="num">
            <input className="input--figure num" value={row.amountExcl} disabled={busy} inputMode="decimal"
                   onChange={(e) => onChange({ amountExcl: e.target.value })} />
          </td>
          <td className="num">
            <input className="input--figure num" value={row.amountIncl} disabled={busy} inputMode="decimal"
                   onChange={(e) => onChange({ amountIncl: e.target.value })} />
          </td>
          <td className="num">
            <button className="icon-btn icon-btn--tight" disabled={busy} onClick={onRemove}
                    title={`Delete line ${line.lineNo} (Ctrl-D)`}>×</button>
          </td>
        </>
      ) : (
        <>
          <td className="figure">{line.transactionDate}</td>
          <td className="figure code">{gcs(line.contraKind)}</td>
          <td>{contraName(line, accounts, counterparties)}</td>
          <td className="figure code">{line.isReconciled ? 'Y' : 'N'}</td>
          <td className="figure code">
            {line.reference ?? '—'}
            {line.referenceGenerated && <AutoMark />}
          </td>
          <td>
            {line.description}
            {line.descriptionOriginal != null && <OriginalDescription text={line.descriptionOriginal} />}
            {/* The ABSENCE is what is worth saying. Every line of a posted
                batch produced its own journal entry — that is decision 5 —
                so a posted line with no entry is the one thing on this table
                a reader must not scroll past. */}
            {!line.entryId && batchPosted && (
              <div className="row-refusal">posted with no journal entry</div>
            )}
            {isMatchable(line) && <div className="row-note">{settlementNote(line, allocations)}</div>}
          </td>
          <td className="figure code">{taxCodeLabel(taxCodes, line)}</td>
          <td className="num"><Money value={line.amountExcl} /></td>
          <td className="num"><Money value={line.amountIncl} /></td>
        </>
      )}
    </tr>
  );
}

/** The small "auto" mark on a line whose reference the server generated. */
function AutoMark() {
  return (
    <span className="ref-auto"
          title="Assigned automatically — editing it replaces the generated number">auto</span>
  );
}

/**
 * The icon beside a description the clean-up rules changed, and the little panel
 * it opens with what the bank actually wrote (`descriptionOriginal`).
 *
 * A button that toggles, and nothing that takes focus away: the panel is not
 * focusable and sits inside the same row, so opening it never ends the row's
 * edit and Tab still moves on to the next cell. The text is selectable. It
 * closes on a second click, on Escape, or on a press anywhere outside it — and
 * it is read-only by nature, so it works the same on a posted batch.
 */
function OriginalDescription({ text }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <span className="orig" ref={box}>
      <button type="button" className="icon-btn icon-btn--tight orig__btn"
              title="Show original bank description" aria-label="Show original bank description"
              aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        ↺
      </button>
      {open && (
        <span className="orig__pop" role="note">
          <span className="orig__label">Original bank description</span>
          <span className="orig__text">{text}</span>
          <span className="orig__why">
            Shortened by your description clean-up rules when the statement was imported.
          </span>
        </span>
      )}
    </span>
  );
}

/**
 * A column header that sorts the grid: click, Enter or Space cycles ascending,
 * descending, capture order. `aria-sort` is only ever on the active column.
 */
function SortTh({ k, sort, onSort, cw, className = '', children, ...rest }) {
  const active = sort?.key === k;
  const toggle = () => onSort(nextSort(sort, k));
  return (
    <th
      {...rest}
      className={`${className} th--sortable`.trim()}
      tabIndex={0}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggle(); }
      }}
    >
      {children}
      {active && <span className="sort-mark" aria-hidden="true">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
      {/* The drag handle on the header's right edge. Not focusable and not in the
          tab order, so the header's own Enter/Space sort is untouched; its click
          and double-click are stopped here so grabbing the edge never sorts. */}
      <span
        className="col-resize"
        aria-hidden="true"
        title="Drag to resize — double-click to reset this column"
        onPointerDown={(e) => cw.startDrag(k, e)}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => { e.stopPropagation(); cw.resetColumn(k); }}
      />
    </th>
  );
}

/**
 * Resizable columns for the capture grid; the pure half is lib/columnWidths.js.
 *
 * NO PER-MOUSEMOVE RE-RENDER: while the pointer moves, the column's <col> and
 * the table's width are written straight to the DOM, and React state (and the
 * viewer's localStorage) is only touched on release. A press that never moved
 * commits nothing, so a double-click or a stray click cannot turn the auto
 * Description column into a fixed one. Pointer events, so touch works, and the
 * pointer is captured by the handle so the drag survives leaving the header.
 */
function useColumnWidths(isOpen) {
  const [saved, setSaved] = useState(() => loadWidths(safeStorage()));
  const savedRef = useRef(saved);
  savedRef.current = saved;
  const colEls = useRef({});
  const tableRef = useRef(null);

  const columns = gridColumns(isOpen);
  const widths = effectiveWidths(saved, columns);

  const commit = (next) => { setSaved(next); saveWidths(safeStorage(), next); };

  const startDrag = (key, e) => {
    if (e.button !== undefined && e.button !== 0) return;   // primary button, or touch/pen contact
    e.preventDefault();
    e.stopPropagation();
    const handle = e.currentTarget;
    const startX = e.clientX;
    const start = handle.parentElement.getBoundingClientRect().width;
    let latest = start;
    let moved = false;
    try { handle.setPointerCapture(e.pointerId); } catch { /* not every environment can */ }
    document.body.classList.add('col-resizing');

    const move = (ev) => {
      moved = true;
      latest = clampWidth(start + ev.clientX - startX);
      const col = colEls.current[key];
      if (col) col.style.width = `${latest}px`;
      const table = tableRef.current;
      if (table) {
        const t = tableWidthStyle({ ...effectiveWidths(savedRef.current, columns), [key]: latest });
        table.style.minWidth = `${t.minWidth}px`;
        table.style.width = t.width === undefined ? '' : `${t.width}px`;
      }
    };
    const end = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      document.body.classList.remove('col-resizing');
      try { handle.releasePointerCapture(e.pointerId); } catch { /* already released */ }
      if (moved) commit({ ...savedRef.current, [key]: latest });
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  };

  return {
    columns,
    widths,
    tableRef,
    tableStyle: tableWidthStyle(widths),
    colRef: (key) => (el) => { colEls.current[key] = el; },
    anySaved: Object.keys(saved).length > 0,
    startDrag,
    resetColumn: (key) => commit(withoutWidth(savedRef.current, key)),
    resetAll: () => commit({}),
  };
}

/** The row being typed at the bottom of the grid. Enter saves it. */
function DraftRow({
  row, busy, accounts, tb, entity, onAccountCreated, taxCodes, counterparties, periods, bankAccountId, direction,
  proposal, refHint, dateRef, onFocus, onChange, onSave,
}) {
  const period = periodForDate(periods, row.transactionDate);
  // THE PROPOSAL BELONGS IN THIS CHECK TOO, and forgetting it here would be the
  // whole feature switched off from one line: Enter and tab-away both refuse to
  // save a row `rowProblem` complains about, so a row a rule is about to code
  // would sit there being told it needs the account the rule is holding.
  const problem = isRowEmpty(row) ? null : rowProblem(row, proposal);

  const onKeyDown = (e) => {
    // Enter saves from anywhere in the row — Pastel's own behaviour, and what
    // makes a statement typeable without reaching for the mouse.
    if (e.key !== 'Enter' || busy) return;
    e.preventDefault();
    if (!isRowEmpty(row) && !problem) onSave();
  };

  return (
    <tr className="row--draft" onFocus={onFocus} onKeyDown={onKeyDown}
        onBlur={(e) => {
          // Tabbing off a finished row saves it, the way the grid above does.
          if (e.currentTarget.contains(e.relatedTarget)) return;
          if (!isRowEmpty(row) && !problem) onSave();
        }}>
      <td className="figure lineno">+</td>
      <td className="figure code" title={period ? `${period.label} · ${period.status}` : undefined}>
        {periodTag(period)}
      </td>
      <td>
        <input ref={dateRef} type="date" value={row.transactionDate} disabled={busy}
               onChange={(e) => onChange({ transactionDate: e.target.value })} />
      </td>
      <td>
        <select value={row.contraKind} disabled={busy}
                onChange={(e) => onChange({ contraKind: e.target.value })}>
          {CONTRA_KINDS.map(([k, letter, label]) => (
            <option key={k} value={k} title={label}>{letter}</option>
          ))}
        </select>
      </td>
      <td>
        <ContraPicker row={row} accounts={accounts} tb={tb} entity={entity}
                      onAccountCreated={onAccountCreated} counterparties={counterparties}
                      bankAccountId={bankAccountId} busy={busy} onChange={onChange} />
      </td>
      <td className="figure code">N</td>
      <td>
        {/* With a prefix this stays EMPTY and the number the server will give is
            the grey hint; typing here sends your own instead. Without one it is
            seeded and counted up client-side exactly as it always was. */}
        <input value={row.reference} disabled={busy} maxLength={REFERENCE_MAX}
               placeholder={refHint.placeholder} title={refHint.title}
               aria-label={refHint.auto ? 'Reference — leave blank to assign automatically' : undefined}
               onChange={(e) => onChange({ reference: e.target.value })} />
      </td>
      <td>
        <input value={row.description} disabled={busy} placeholder="What the payment was for"
               onChange={(e) => onChange({ description: e.target.value })} />
        {problem && <div className="row-refusal">{problem}</div>}
      </td>
      <td>
        <TaxPicker row={row} taxCodes={taxCodes} direction={direction} busy={busy} onChange={onChange} />
      </td>
      <td className="num">
        <input className="input--figure num" value={row.amountExcl} disabled={busy} inputMode="decimal"
               placeholder="0.00" onChange={(e) => onChange({ amountExcl: e.target.value })} />
      </td>
      <td className="num">
        <input className="input--figure num" value={row.amountIncl} disabled={busy} inputMode="decimal"
               placeholder="0.00" onChange={(e) => onChange({ amountIncl: e.target.value })} />
      </td>
      <td className="num">
        <button className="icon-btn icon-btn--tight" disabled={busy || isRowEmpty(row) || Boolean(problem)}
                onClick={onSave} title="Add this line (Enter)">↵</button>
      </td>
    </tr>
  );
}

const NEW_ACCOUNT = '__new__';

/** The Account column: a GL account, or the customer or supplier being settled. */
function ContraPicker({
  row, accounts, tb, entity, onAccountCreated, counterparties, bankAccountId, busy, onChange,
}) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', subtype: 'operating_expense' });
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState(null);

  const create = async () => {
    const body = newAccountBody(form);
    if (body.problem) { setProblem(body.problem); return; }
    setWorking(true); setProblem(null);
    try {
      const { account } = await api.post(`/api/entities/${entity.id}/accounts`, body);
      if (onAccountCreated) await onAccountCreated();
      onChange({ contraAccountId: account.id });
      setForm({ code: '', name: '', subtype: 'operating_expense' });
      setAdding(false);
    } catch (e) { setProblem(e.message); } finally { setWorking(false); }
  };

  if (row.contraKind === 'ledger') {
    return (
      <>
        <select value={row.contraAccountId} disabled={busy}
                onChange={(e) => {
                  if (e.target.value === NEW_ACCOUNT) { setAdding(true); return; }
                  onChange({ contraAccountId: e.target.value });
                }}>
          <option value="">— account —</option>
          {entity && <option value={NEW_ACCOUNT}>+ New account…</option>}
          {contraOptions(accounts, bankAccountId).map((a) => (
            <option key={a.id} value={a.id}>{contraOptionLabel(a, tb, money)}</option>
          ))}
        </select>
        {adding && (
          <div className="new-account"
               onKeyDown={(e) => {
                 e.stopPropagation();
                 if (e.key === 'Enter') { e.preventDefault(); create(); }
               }}>
            <input value={form.code} placeholder="Code" maxLength={20} disabled={working}
                   onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
            <input value={form.name} placeholder="Account name" disabled={working}
                   onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <select value={form.subtype} disabled={working}
                    onChange={(e) => setForm((f) => ({ ...f, subtype: e.target.value }))}>
              {NEW_ACCOUNT_KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <button type="button" className="btn" disabled={working} onClick={create}>Create account</button>
            <button type="button" className="btn btn--quiet" disabled={working}
                    onClick={() => { setAdding(false); setProblem(null); }}>Cancel</button>
            {problem && <div className="row-refusal">{problem}</div>}
          </div>
        )}
      </>
    );
  }
  return (
    <select value={row.counterpartyId} disabled={busy}
            onChange={(e) => onChange({ counterpartyId: e.target.value })}>
      <option value="">— {row.contraKind} —</option>
      {counterpartyOptions(counterparties, row.contraKind).map((c) => (
        <option key={c.id} value={c.id}>{c.code} {c.name}</option>
      ))}
    </select>
  );
}

/**
 * The Tax column.
 *
 * DISABLED ON A C OR S ROW, and that is spec §3D rather than tidiness: the VAT
 * on a subledger transaction was declared when the invoice was posted, and
 * declaring it again at settlement puts the same supply on the VAT201 twice
 * with both halves individually well-formed. The database CHECK is what holds
 * when someone posts through /api/v1; this is what stops it being typed.
 */
function TaxPicker({ row, taxCodes, direction, busy, onChange }) {
  if (row.contraKind !== 'ledger') {
    return <span className="cashbook__unknown" title="the VAT was declared when the invoice was posted">—</span>;
  }
  return (
    <select value={row.taxCodeId} disabled={busy}
            onChange={(e) => onChange({ taxCodeId: e.target.value })}>
      <option value="">00</option>
      {taxCodeOptions(taxCodes, direction).map((t) => (
        <option key={t.id} value={t.id} title={t.name}>{t.code}</option>
      ))}
    </select>
  );
}

/**
 * The VAT split, per code.
 *
 * Not decoration: a batch total that agrees with the statement while the VAT
 * split is wrong is exactly the failure decision 4 exists to catch, and these
 * subtotals are the only place it is visible before posting.
 */
function TaxSplit({ totals }) {
  return (
    <div style={{ marginTop: 22 }}>
      <h2 className="display" style={{ fontSize: 17, margin: '0 0 8px' }}>What this batch holds</h2>
      <div className="scroll-x">
        <table className="ledger">
          <thead>
            <tr>
              <th style={{ width: 120 }}>VAT code</th>
              <th className="num" style={{ width: 80 }}>Lines</th>
              <th className="num" style={{ width: 150 }}>Exclusive</th>
              <th className="num" style={{ width: 150 }}>VAT</th>
              <th className="num" style={{ width: 150 }}>Inclusive</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {totals.byTaxCode.map((g) => (
              <tr key={g.taxCodeId ?? 'none'}>
                <td className="code figure">{g.code ?? 'No VAT'}</td>
                <td className="num figure">{g.lineCount}</td>
                <td className="num"><Money value={g.excl} /></td>
                <td className="num"><Money value={g.vat} /></td>
                <td className="num"><Money value={g.incl} /></td>
                <td />
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Batch total</td>
              <td className="num figure">{totals.lineCount}</td>
              <td className="num"><Money value={totals.excl} /></td>
              <td className="num"><Money value={totals.vat} /></td>
              <td className="num"><Money value={totals.incl} /></td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="note">
        The inclusive total is what the bank statement should say, and exactly the movement this
        batch causes on the cash book. Those being one number is the point of the whole screen.
      </div>
    </div>
  );
}

/** What the post actually did — one journal entry per line, as promised. */
function Posted({ result }) {
  return (
    <div className="ok" style={{ marginTop: 18 }}>
      <strong>Batch {result.number} posted.</strong>{' '}
      {result.lines.length} line{result.lines.length === 1 ? '' : 's'}, totalling{' '}
      {money(result.totals.incl)}, each posted as its own journal entry:{' '}
      {result.lines.slice(0, 8).map((l) => l.entryNo).filter(Boolean).join(', ')}
      {result.lines.length > 8 ? ', …' : ''}.
    </div>
  );
}

/** A posted line's contra, by name — the pickers are gone by then. */
function contraName(line, accounts, counterparties) {
  if (line.contraKind === 'ledger') {
    const a = accounts.find((x) => x.id === line.contraAccountId);
    return a ? <><span className="code figure">{a.code}</span> {a.name}</> : '—';
  }
  const c = counterparties.find((x) => x.id === line.counterpartyId);
  return c ? <><span className="code figure">{c.code}</span> {c.name}</> : '—';
}

/**
 * The Tax cell of a line the batch has posted.
 *
 * A subledger line reads `—` AND NOT `00`: it does not carry a code at all
 * (spec §3D), and "00" would say it was captured as a zero-rated supply. A
 * ledger line with no code genuinely is Pastel's 00.
 */
const taxCodeLabel = (taxCodes, line) => {
  if (line.contraKind !== 'ledger') return '—';
  if (!line.taxCodeId) return '00';
  return taxCodes.find((t) => t.id === line.taxCodeId)?.code ?? '—';
};
